import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

// Annuity Payout Calculator (distribution phase) — mirrors reference image
// Tabs: Fixed length, Fixed payment
export default function AnnuityPayoutCalc() {
  const [tab, setTab] = useState('length'); // 'length' | 'payment'
  const [principal, setPrincipal] = useState(500000);
  const [rate, setRate] = useState(6);
  const [years, setYears] = useState(10);
  const [payoutAmt, setPayoutAmt] = useState(5000);
  const [freq, setFreq] = useState('monthly'); // annually | semiannually | quarterly | monthly

  const perYear = { annually: 1, semiannually: 2, quarterly: 4, monthly: 12 }[freq];
  const freqLabel = { annually: 'annually', semiannually: 'semiannually', quarterly: 'quarterly', monthly: 'monthly' }[freq];

  const c = useMemo(() => {
    // Effective periodic rate matching compounding at same frequency
    const iP = Math.pow(1 + rate / 100, 1 / perYear) - 1;

    if (tab === 'length') {
      // Fixed length: solve payout PMT given principal P, periods n, rate iP
      const n = Math.max(0, Math.round(years * perYear));
      const PMT = iP === 0 ? principal / n : (principal * iP) / (1 - Math.pow(1 + iP, -n));
      // Amortization
      const yearly = [];
      let bal = principal;
      for (let y = 1; y <= years; y++) {
        const begin = bal;
        let yInterest = 0;
        for (let p = 0; p < perYear; p++) {
          const interest = bal * iP;
          yInterest += interest;
          bal = bal + interest - PMT;
        }
        if (bal < 0.005) bal = 0;
        yearly.push({ year: y, begin, interest: yInterest, end: bal });
        if (bal <= 0) break;
      }
      const totalPayments = PMT * n;
      const totalReturn = totalPayments - principal;
      return { PMT, n, yearly, totalPayments, totalReturn, years, mode: 'length' };
    } else {
      // Fixed payment: solve n given principal, iP, payment
      const P = payoutAmt;
      let n;
      if (iP === 0) n = Math.floor(principal / P);
      else if (P <= principal * iP) n = Infinity; // will never deplete
      else n = Math.log(P / (P - principal * iP)) / Math.log(1 + iP);
      const nInt = isFinite(n) ? Math.ceil(n) : 0;
      const yearsOut = isFinite(n) ? n / perYear : Infinity;
      const yearly = [];
      let bal = principal;
      const maxYears = isFinite(yearsOut) ? Math.ceil(yearsOut) : 0;
      for (let y = 1; y <= maxYears; y++) {
        const begin = bal;
        let yInterest = 0;
        for (let p = 0; p < perYear; p++) {
          const interest = bal * iP;
          yInterest += interest;
          const pay = Math.min(P, bal + interest);
          bal = bal + interest - pay;
          if (bal < 0.005) { bal = 0; break; }
        }
        yearly.push({ year: y, begin, interest: yInterest, end: bal });
        if (bal <= 0) break;
      }
      const totalPayments = isFinite(n) ? P * n : Infinity;
      const totalReturn = isFinite(totalPayments) ? totalPayments - principal : Infinity;
      return { PMT: P, n: nInt, yearly, totalPayments, totalReturn, years: yearsOut, mode: 'payment' };
    }
  }, [tab, principal, rate, years, payoutAmt, perYear]);

  const freqNoun = { annually: 'annual', semiannually: 'semi-annual', quarterly: 'quarterly', monthly: 'monthly' }[freq];

  return (
    <CalcShell look="cnet" title="Annuity Payout Calculator" subtitle="Distribution phase — how much can you withdraw from an annuity?"
      about={<>
        <p>The <b>Annuity Payout Calculator</b> computes how much you can withdraw periodically from an accumulated annuity balance, or how long a target payout will last, given an assumed return during the payout period.</p>
        <p>Use <b>Fixed length</b> when you want to draw the fund down over a specific number of years; use <b>Fixed payment</b> when you have a required withdrawal amount and want to know how long it will last.</p>
      </>}
      slug="annuity-payout"
    >
      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6 -mx-1">
        {[['length','Fixed length'],['payment','Fixed payment']].map(([k,l]) => (
          <button key={k} onClick={()=>setTab(k)} data-testid={`annpay-tab-${k}`}
            className={`px-6 py-2 text-sm font-semibold border-b-2 -mb-px ${tab===k?'text-blue-800 border-blue-800 bg-white':'text-slate-500 border-transparent hover:text-slate-700'}`}>{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div>
          <div className="field">
            <label>Starting principal</label>
            <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
              <input type="number" value={principal} onChange={e=>setPrincipal(+e.target.value||0)} className="flex-1" data-testid="annpay-principal"/></div>
          </div>
          <div className="field">
            <label>Interest/return rate</label>
            <div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1" data-testid="annpay-rate"/><span className="self-center text-slate-500">%</span></div>
          </div>
          {tab === 'length' ? (
            <div className="field">
              <label>Years to payout</label>
              <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)} className="flex-1" data-testid="annpay-years"/><span className="self-center text-slate-500">years</span></div>
            </div>
          ) : (
            <div className="field">
              <label>Payout amount every period</label>
              <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
                <input type="number" value={payoutAmt} onChange={e=>setPayoutAmt(+e.target.value||0)} className="flex-1" data-testid="annpay-amount"/></div>
            </div>
          )}
          <div className="field">
            <label>Payout frequency</label>
            <select value={freq} onChange={e=>setFreq(e.target.value)} data-testid="annpay-freq">
              <option value="annually">Annually</option>
              <option value="semiannually">Semi-annually</option>
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>

        {/* Result */}
        <div>
          <div className="result-panel">
            <div className="result-title">Result</div>
            {c.mode === 'length' ? (
              <>
                <div className="text-slate-700 mb-2">You can withdraw <b className="text-blue-800 text-lg">{fmt(c.PMT)}</b> {freqLabel}.</div>
                <div className="text-slate-700">Total of {c.n} payments: <b>{fmt(c.totalPayments)}</b></div>
                <div className="text-slate-700">Total interest/return: <b>{fmt(c.totalReturn)}</b></div>
              </>
            ) : isFinite(c.n) && c.n > 0 ? (
              <>
                <div className="text-slate-700 mb-2">You can withdraw <b>{fmt(payoutAmt)}</b> {freqLabel} for <b className="text-blue-800 text-lg">{c.years.toFixed(2)} years</b> ({c.n} payments).</div>
                <div className="text-slate-700">Total payout: <b>{fmt(c.totalPayments)}</b></div>
                <div className="text-slate-700">Total interest/return: <b>{fmt(c.totalReturn)}</b></div>
              </>
            ) : (
              <div className="text-slate-700">At this return rate, the principal <b>never depletes</b> — interest alone covers the payment.</div>
            )}
          </div>
          <div className="mt-4">
            <PieChart parts={[
              { label: 'Starting principal', value: principal, color: '#2563eb' },
              { label: 'Interest/return', value: Math.max(0, isFinite(c.totalReturn) ? c.totalReturn : 0), color: '#10b981' },
            ]} />
          </div>
        </div>
      </div>

      {/* Balance graph */}
      {c.yearly.length > 0 && (
        <div className="mt-8">
          <h3 className="font-bold text-slate-900 mb-3">Balance over time</h3>
          <PayoutGraph yearly={c.yearly} principal={principal} />
        </div>
      )}

      {/* Annuity Balances table */}
      {c.yearly.length > 0 && (
        <div className="mt-8">
          <h3 className="font-bold text-slate-900 mb-3">Annuity Balances</h3>
          <div className="overflow-auto rounded-xl border border-slate-200 max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-blue-800 text-white sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Year</th>
                  <th className="px-3 py-2 text-right">Beginning balance</th>
                  <th className="px-3 py-2 text-right">Interest/return</th>
                  <th className="px-3 py-2 text-right">Ending balance</th>
                </tr>
              </thead>
              <tbody>
                {c.yearly.map((r,i)=>(
                  <tr key={i} className={`border-b border-slate-100 ${i%2?'bg-slate-50':''}`}>
                    <td className="px-3 py-2">{r.year}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.begin)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{fmt(r.interest)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(r.end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <StepsPanel steps={c.mode === 'length' ? [
        `Convert annual rate to per-period rate: iP = (1 + ${rate}%)^(1/${perYear}) − 1 = ${((Math.pow(1+rate/100,1/perYear)-1)*100).toFixed(4)}%`,
        `Number of ${freqNoun} periods n = ${years} × ${perYear} = ${years*perYear}`,
        `Payment = P × iP / (1 − (1+iP)^-n) = <b>${fmt(c.PMT)}</b> per ${freqNoun} period`,
        `Total paid out over ${c.n} periods ≈ <b>${fmt(c.totalPayments)}</b>`
      ] : [
        `Per-period rate iP ≈ ${((Math.pow(1+rate/100,1/perYear)-1)*100).toFixed(4)}%`,
        `n = ln(PMT / (PMT − P·iP)) / ln(1+iP) periods`,
        isFinite(c.n) ? `Fund lasts ≈ <b>${c.years.toFixed(2)} years</b> (${c.n} payments)` : `PMT ≤ P·iP → fund never depletes`
      ]} />
    </CalcShell>
  );
}

function PayoutGraph({ yearly, principal }) {
  if (!yearly.length) return null;
  const w = 640, h = 260, padL = 60, padR = 20, padT = 15, padB = 38;
  const n = yearly.length;
  let cumI = 0;
  const pts = yearly.map((y, i) => { cumI += y.interest; return { x: i + 1, balance: y.end, interest: cumI }; });
  const max = Math.max(principal, ...pts.map(p => p.balance));
  const xs = (i) => padL + (i / n) * (w - padL - padR);
  const ys = (v) => h - padB - (v / max) * (h - padT - padB);
  const balLine = `${xs(0)},${ys(principal)} ` + pts.map((p, i) => `${xs(i + 1)},${ys(p.balance)}`).join(' ');
  const intLine = `${xs(0)},${ys(0)} ` + pts.map((p, i) => `${xs(i + 1)},${ys(p.interest)}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * max);
  const xTicks = Math.min(6, n);
  return (
    <div className="overflow-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[500px]">
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={ys(t)} x2={w - padR} y2={ys(t)} stroke="#e2e8f0" strokeDasharray="3 3" />
            <text x={padL - 8} y={ys(t) + 4} textAnchor="end" fontSize="10" fill="#64748b">${Math.round(t/1000)}K</text>
          </g>
        ))}
        <line x1={padL} y1={h - padB} x2={w - padR} y2={h - padB} stroke="#94a3b8" />
        {Array.from({ length: xTicks + 1 }, (_, i) => {
          const yr = Math.round(i * n / xTicks);
          return <text key={i} x={xs(yr)} y={h - padB + 16} textAnchor="middle" fontSize="10" fill="#64748b">{yr}</text>;
        })}
        <text x={w/2} y={h-4} textAnchor="middle" fontSize="11" fill="#334155">Year</text>
        <polyline points={balLine} fill="none" stroke="#2563eb" strokeWidth="2.5" />
        <polyline points={intLine} fill="none" stroke="#10b981" strokeWidth="2.5" />
      </svg>
      <div className="flex flex-wrap gap-4 justify-center mt-2 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#2563eb'}}/>Balance</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#10b981'}}/>Cumulative interest/return</span>
      </div>
    </div>
  );
}
